import { useEffect, useState } from "react";
import classnames from "classnames";
import PropTypes from "prop-types";
import styles from "./tile.module.css";
import ProductImageRotation from "../../../helpers/product-image-rotation";
import {
  discoArrayToProductImageStruct,
  ProductImageSize,
  getProductImageAltText,
} from "helpers/product-image-helpers";
import ProductImage from "../shared/product-image";
import MaternityHelper from "helpers/maternity-helper";
import { useSelector } from "react-redux";
import { FetchPriority } from "rtr-constants";

/**
 * Strips the host and protocol from a URL
 * @param url The url to strip
 * @returns {string} The original URL's pathname
 */
function formatImagesUrlInProduct(images) {
  return images.map(imgUrl => imgUrl.replace("270x", "1080x"));
}

/*
 * This function converts the product image array into an Object with appropriate keys like "front", "nomodel" etc
 * to help identify different urls easily
 */
function convertImageArrayToObject(product) {
  return {
    ...product,
    images: discoArrayToProductImageStruct(formatImagesUrlInProduct(product.images)),
  };
}

function getImageGroups(product, filteredSizes, userData, noModelImageFirst = false) {
  if (!product.images) {
    return [];
  }

  const showMaternityBumpImage = MaternityHelper.showMaternityBumpImage(userData);
  const imageRotation = ProductImageRotation(filteredSizes, product, showMaternityBumpImage, noModelImageFirst);
  return imageRotation.map(key => product.images[key]).filter(Boolean);
}

export function GridTileImage({ active, label, ...props }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const filteredSizes = useSelector(state => state?.workingFilters?.canonicalSizes || []);
  const isMobileViewport = useSelector(state => state?.browser?.isMobileViewport);
  const userData = useSelector(state => state?.userData);
  const product = convertImageArrayToObject(props.product);
  const imageGroups = getImageGroups(product, filteredSizes, userData);
  const showImageGroups = isMobileViewport ? [imageGroups[0]] : imageGroups;

  useEffect(() => {
    let interval;
    if (isHovered) {
      interval = setInterval(() => {
        setCurrentImageIndex(prevIndex => {
          return (prevIndex + 1) % showImageGroups.length;
        });
      }, 1500);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [isHovered]);

  const Images = () => {
    return showImageGroups.map((imageGroup, index) => (
      <ProductImage
        className={classnames(styles.tile__image, {
          [styles.hidden]: index !== currentImageIndex,
          [styles.visible]: index === currentImageIndex,
        })}
        altText={getProductImageAltText(product?.displayName, product?.designerDisplayName) + " - Photo " + index}
        key={`"prod-image-${index}`}
        imageGroup={imageGroup}
        imageSize={ProductImageSize.x800}
        srcSetSizes={[ProductImageSize.x270, ProductImageSize.x480, ProductImageSize.x800, ProductImageSize.x1080]}
        sizes={"(max-width: 950px) 50vw, (max-width: 1280px) 34vw, 25vw"}
        loading={index === 0 ? "eager" : "lazy"}
        fetchPriority={props.isFirstProduct && index === 0 ? FetchPriority.HIGH : FetchPriority.LOW}
      />
    ));
  };

  return (
    /* eslint-disable-next-line jsx-a11y/no-static-element-interactions */
    <div
      className={classnames(styles.tile)}
      onMouseEnter={() => {
        setIsHovered(true);
      }}
      onMouseLeave={() => setIsHovered(false)}>
      {showImageGroups.length > 0 ? <div>{Images()}</div> : null}
    </div>
  );
}

GridTileImage.propTypes = {
  product: PropTypes.object,
  isFirstProduct: PropTypes.bool,
  isInteractive: PropTypes.bool,
  active: PropTypes.bool,
  label: PropTypes.shape({
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    amount: PropTypes.number.isRequired,
    comparableAmount: PropTypes.number,
    comparableText: PropTypes.string,
    currencyCode: PropTypes.string.isRequired,
    discount: PropTypes.number,
    position: PropTypes.oneOf(["bottom", "center"]),
  }),
};
